import React, { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import api from "../api";
import "./Tiers.css";

const TIER_INFO = {
  low:    { label: "Low",    color: "#2563eb", bg: "rgba(37,99,235,0.08)",   border: "rgba(37,99,235,0.2)",   icon: "●" },
  medium: { label: "Medium", color: "#d97706", bg: "rgba(217,119,6,0.08)",   border: "rgba(217,119,6,0.2)",   icon: "●●" },
  high:   { label: "High",   color: "#16a34a", bg: "rgba(22,163,74,0.08)",   border: "rgba(22,163,74,0.2)",   icon: "●●●" },
};

const LEAD_LABELS = {
  self_generated:   "Self Generated",
  partner_referral: "Partner Referral",
  client_referral:  "Client Referral",
  company_referral: "Company Referral",
};

const STATUS_STYLES = {
  pending:          { color: "#d97706", bg: "rgba(217,119,6,0.08)",   label: "Pending Manager" },
  manager_approved: { color: "#2563eb", bg: "rgba(37,99,235,0.08)",   label: "Pending Admin" },
  approved:         { color: "#16a34a", bg: "rgba(22,163,74,0.08)",   label: "Approved" },
  rejected:         { color: "#dc2626", bg: "rgba(220,38,38,0.08)",   label: "Rejected" },
};

export default function Tiers() {
  const { user } = useAuth();
  const [payouts, setPayouts]         = useState(null);
  const [myRequests, setMyRequests]   = useState([]);
  const [pending, setPending]         = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showForm, setShowForm]       = useState(false);
  const [form, setForm]               = useState({ requested_tier: "", reason: "" });
  const [submitting, setSubmitting]   = useState(false);
  const [tab, setTab]                 = useState("overview");
  const [actionComment, setActionComment] = useState({});

  const isLO      = user?.role === "loan_officer";
  const isManager = user?.role === "manager";
  const isAdmin   = user?.role === "admin";

  const load = async () => {
    try {
      const [payoutRes] = await Promise.all([api.get("/tiers/payouts")]);
      setPayouts(payoutRes.data);
      if (isLO) {
        const r = await api.get("/tiers/my-requests");
        setMyRequests(r.data);
      }
      if (isManager || isAdmin) {
        const r = await api.get("/tiers/pending");
        setPending(r.data);
      }
      if (isAdmin) {
        const r = await api.get("/tiers/all");
        setAllRequests(r.data);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => { load(); }, []);

  const handleRequest = async () => {
    if (!form.requested_tier) return;
    setSubmitting(true);
    try {
      await api.post("/tiers/request", form);
      setShowForm(false);
      setForm({ requested_tier: "", reason: "" });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Error submitting request");
    } finally { setSubmitting(false); }
  };

  const handleDecision = async (id, type, decision) => {
    const comment = actionComment[id] || "";
    try {
      await api.post(`/tiers/${id}/${type}-decision`, { decision, comment });
      setActionComment(prev => { const n = {...prev}; delete n[id]; return n; });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Error");
    }
  };

  const currentTierInfo = TIER_INFO[user?.current_tier || "low"];

  if (loading) return <div className="loading">Loading tier information...</div>;

  return (
    <div className="tiers-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Pricing Tiers</h1>
          <p className="page-subtitle">Commission rates by lead source and tier</p>
        </div>
        {isLO && (
          <button
            className="btn-secondary btn-sm"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? "✕ Cancel" : "Request Tier Change"}
          </button>
        )}
      </div>

      {/* Current tier banner for LOs */}
      {isLO && (
        <div className="current-tier-banner" style={{ borderColor: currentTierInfo.color, background: currentTierInfo.bg }}>
          <div className="tier-banner-left">
            <span className="tier-banner-icon" style={{ color: currentTierInfo.color }}>{currentTierInfo.icon}</span>
            <div>
              <div className="tier-banner-label">Your Current Tier</div>
              <div className="tier-banner-value" style={{ color: currentTierInfo.color }}>
                {currentTierInfo.label} Tier
              </div>
            </div>
          </div>
          {user?.nmls_number && (
            <div className="tier-banner-nmls">
              <div className="tier-banner-label">NMLS #</div>
              <div className="tier-banner-value">{user.nmls_number}</div>
            </div>
          )}
          <div className="tier-banner-note">
            Tier changes require manager + admin approval and can be requested once per quarter.
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tier-tabs">
        <button className={`tier-tab ${tab === "overview" ? "active" : ""}`} onClick={() => setTab("overview")}>
          Payout Table
        </button>
        {isLO && (
          <button className={`tier-tab ${tab === "history" ? "active" : ""}`} onClick={() => setTab("history")}>
            My Requests {myRequests.length > 0 && <span className="tab-count">{myRequests.length}</span>}
          </button>
        )}
        {(isManager || isAdmin) && (
          <button className={`tier-tab ${tab === "pending" ? "active" : ""}`} onClick={() => setTab("pending")}>
            Pending Approval {pending.length > 0 && <span className="tab-count tab-count-red">{pending.length}</span>}
          </button>
        )}
        {isAdmin && (
          <button className={`tier-tab ${tab === "all" ? "active" : ""}`} onClick={() => setTab("all")}>
            All Requests
          </button>
        )}
      </div>

      {/* Request form */}
      {showForm && isLO && (
        <div className="tier-request-card card">
          <h3>Request a Tier Change</h3>
          <p className="form-note">
            This request will go to your manager first, then to an admin for final approval.
            You can only submit one request per quarter.
          </p>
          <div className="tier-select-grid">
            {["low","medium","high"].map(tier => {
              const info = TIER_INFO[tier];
              const isCurrent = tier === user?.current_tier;
              return (
                <button
                  key={tier}
                  className={`tier-select-card ${form.requested_tier === tier ? "selected" : ""} ${isCurrent ? "current" : ""}`}
                  style={form.requested_tier === tier ? { borderColor: info.color, background: info.bg } : {}}
                  onClick={() => !isCurrent && setForm({...form, requested_tier: tier})}
                  disabled={isCurrent}
                >
                  <div className="tier-select-name" style={{ color: form.requested_tier === tier ? info.color : undefined }}>
                    {info.label} Tier
                  </div>
                  {isCurrent && <div className="tier-current-tag">Current</div>}
                  <div className="tier-select-rates">
                    {payouts && Object.entries(LEAD_LABELS).map(([key, label]) => (
                      <div key={key} className="tier-rate-row">
                        <span>{label}</span>
                        <span className="tier-rate-val">{payouts.payouts[key][tier]} bps</span>
                      </div>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="form-group" style={{marginTop:16}}>
            <label>Reason for change <span className="optional-tag">optional</span></label>
            <textarea
              value={form.reason}
              onChange={e => setForm({...form, reason: e.target.value})}
              rows={3}
              placeholder="Why are you requesting this tier change?"
            />
          </div>
          <button
            className="btn-primary"
            onClick={handleRequest}
            disabled={!form.requested_tier || submitting}
            style={{marginTop:8}}
          >
            {submitting ? "Submitting..." : "Submit Tier Change Request"}
          </button>
        </div>
      )}

      {/* Payout table */}
      {tab === "overview" && payouts && (
        <div className="payout-section">
          <div className="table-card">
            <div className="payout-table-header">
              <h3>Commission Rates by Lead Source & Tier</h3>
              <p>All rates shown in basis points (bps). 1 bps = 0.01% of loan amount.</p>
            </div>
            <table className="data-table payout-table">
              <thead>
                <tr>
                  <th>Lead Source</th>
                  <th style={{textAlign:"center"}}>
                    <span className="tier-th" style={{color: TIER_INFO.low.color}}>Low Tier</span>
                  </th>
                  <th style={{textAlign:"center"}}>
                    <span className="tier-th" style={{color: TIER_INFO.medium.color}}>Medium Tier</span>
                  </th>
                  <th style={{textAlign:"center"}}>
                    <span className="tier-th" style={{color: TIER_INFO.high.color}}>High Tier</span>
                  </th>
                  {isLO && <th style={{textAlign:"center"}}>Your Rate</th>}
                </tr>
              </thead>
              <tbody>
                {payouts && Object.entries(LEAD_LABELS).map(([key, label]) => {
                  const isFlat = key === "company_referral";
                  const yourTier = user?.current_tier || "low";
                  return (
                    <tr key={key}>
                      <td>
                        <div className="lead-source-cell">
                          <span className="lead-source-name">{label}</span>
                          {isFlat && <span className="flat-tag">Flat rate</span>}
                        </div>
                      </td>
                      {["low","medium","high"].map(tier => (
                        <td key={tier} style={{textAlign:"center"}}>
                          <span className={`bps-val ${isLO && tier === yourTier ? "your-tier-val" : ""}`}
                                style={isLO && tier === yourTier ? {color: TIER_INFO[tier].color, fontWeight:700} : {}}>
                            {payouts.payouts[key][tier]} bps
                          </span>
                        </td>
                      ))}
                      {isLO && (
                        <td style={{textAlign:"center"}}>
                          <span className="your-rate-badge" style={{
                            background: TIER_INFO[yourTier].bg,
                            color: TIER_INFO[yourTier].color,
                            border: "1px solid " + TIER_INFO[yourTier].border
                          }}>
                            {payouts.payouts[key][yourTier]} bps
                          </span>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="payout-footnote">
              * Company Referral pays a flat 40 bps regardless of tier.
            </div>
          </div>
        </div>
      )}

      {/* My request history */}
      {tab === "history" && isLO && (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>From Tier</th>
                <th>Requested Tier</th>
                <th>Reason</th>
                <th>Manager</th>
                <th>Admin</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {myRequests.length === 0 && (
                <tr><td colSpan={7} className="empty-row">No tier change requests yet</td></tr>
              )}
              {myRequests.map(r => {
                const st = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
                return (
                  <tr key={r.id}>
                    <td>{new Date(r.created_at).toLocaleDateString()}</td>
                    <td><TierBadge tier={r.current_tier} /></td>
                    <td><TierBadge tier={r.requested_tier} /></td>
                    <td style={{color:"var(--text-secondary)",maxWidth:160,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                      {r.reason || "—"}
                    </td>
                    <td>
                      {r.manager_decision
                        ? <span style={{color: r.manager_decision === "approved" ? "var(--success)" : "var(--danger)", fontWeight:600, fontSize:"0.82rem"}}>
                            {r.manager_decision === "approved" ? "✓ Approved" : "✗ Rejected"}
                          </span>
                        : <span style={{color:"var(--text-muted)",fontSize:"0.82rem"}}>Pending</span>
                      }
                    </td>
                    <td>
                      {r.admin_decision
                        ? <span style={{color: r.admin_decision === "approved" ? "var(--success)" : "var(--danger)", fontWeight:600, fontSize:"0.82rem"}}>
                            {r.admin_decision === "approved" ? "✓ Approved" : "✗ Rejected"}
                          </span>
                        : <span style={{color:"var(--text-muted)",fontSize:"0.82rem"}}>
                            {r.status === "manager_approved" ? "Pending" : "—"}
                          </span>
                      }
                    </td>
                    <td>
                      <span className="status-badge" style={{background: st.bg, color: st.color}}>
                        {st.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pending approval */}
      {tab === "pending" && (isManager || isAdmin) && (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Loan Officer</th>
                <th>NMLS</th>
                <th>From</th>
                <th>To</th>
                <th>Reason</th>
                <th>Submitted</th>
                <th>Stage</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pending.length === 0 && (
                <tr><td colSpan={8} className="empty-row">No pending requests</td></tr>
              )}
              {pending.map(r => {
                const needsManager = r.status === "pending" && (isManager || isAdmin);
                const needsAdmin   = r.status === "manager_approved" && isAdmin;
                const actionType   = needsManager ? "manager" : needsAdmin ? "admin" : null;
                return (
                  <tr key={r.id}>
                    <td style={{fontWeight:600}}>{r.loan_officer?.full_name}</td>
                    <td style={{color:"var(--text-muted)",fontFamily:"monospace"}}>{r.loan_officer?.nmls_number || "—"}</td>
                    <td><TierBadge tier={r.current_tier} /></td>
                    <td><TierBadge tier={r.requested_tier} /></td>
                    <td style={{color:"var(--text-secondary)",maxWidth:150,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                      {r.reason || "—"}
                    </td>
                    <td>{new Date(r.created_at).toLocaleDateString()}</td>
                    <td>
                      <span className="status-badge" style={{
                        background: STATUS_STYLES[r.status]?.bg,
                        color: STATUS_STYLES[r.status]?.color
                      }}>
                        {STATUS_STYLES[r.status]?.label}
                      </span>
                    </td>
                    <td>
                      {actionType ? (
                        <div className="approval-cell">
                          <input
                            className="comment-input"
                            placeholder="Comment (optional)"
                            value={actionComment[r.id] || ""}
                            onChange={e => setActionComment(prev => ({...prev, [r.id]: e.target.value}))}
                          />
                          <div style={{display:"flex",gap:6,marginTop:4}}>
                            <button className="btn-approve" onClick={() => handleDecision(r.id, actionType, "approved")}>
                              ✓ Approve
                            </button>
                            <button className="btn-reject" onClick={() => handleDecision(r.id, actionType, "rejected")}>
                              ✗ Reject
                            </button>
                          </div>
                        </div>
                      ) : (
                        <span style={{color:"var(--text-muted)",fontSize:"0.82rem"}}>
                          {isManager && r.status === "manager_approved" ? "Awaiting admin" : "—"}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* All requests (admin) */}
      {tab === "all" && isAdmin && (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Loan Officer</th>
                <th>NMLS</th>
                <th>From</th>
                <th>To</th>
                <th>Requested</th>
                <th>Manager</th>
                <th>Admin</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {allRequests.length === 0 && (
                <tr><td colSpan={8} className="empty-row">No requests found</td></tr>
              )}
              {allRequests.map(r => (
                <tr key={r.id}>
                  <td style={{fontWeight:600}}>{r.loan_officer?.full_name}</td>
                  <td style={{color:"var(--text-muted)",fontFamily:"monospace"}}>{r.loan_officer?.nmls_number || "—"}</td>
                  <td><TierBadge tier={r.current_tier} /></td>
                  <td><TierBadge tier={r.requested_tier} /></td>
                  <td>{new Date(r.created_at).toLocaleDateString()}</td>
                  <td style={{fontSize:"0.82rem",color: r.manager_decision === "approved" ? "var(--success)" : r.manager_decision === "rejected" ? "var(--danger)" : "var(--text-muted)"}}>
                    {r.manager_decision ? (r.manager_decision === "approved" ? "✓ Approved" : "✗ Rejected") : "Pending"}
                  </td>
                  <td style={{fontSize:"0.82rem",color: r.admin_decision === "approved" ? "var(--success)" : r.admin_decision === "rejected" ? "var(--danger)" : "var(--text-muted)"}}>
                    {r.admin_decision ? (r.admin_decision === "approved" ? "✓ Approved" : "✗ Rejected") : "—"}
                  </td>
                  <td>
                    <span className="status-badge" style={{
                      background: STATUS_STYLES[r.status]?.bg,
                      color: STATUS_STYLES[r.status]?.color
                    }}>
                      {STATUS_STYLES[r.status]?.label}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TierBadge({ tier }) {
  if (!tier) return <span style={{color:"var(--text-muted)"}}>—</span>;
  const info = TIER_INFO[tier] || TIER_INFO.low;
  return (
    <span style={{
      background: info.bg,
      color: info.color,
      border: "1px solid " + info.border,
      padding: "3px 10px",
      borderRadius: 20,
      fontSize: "0.75rem",
      fontWeight: 700,
      textTransform: "capitalize"
    }}>
      {info.label}
    </span>
  );
}
